import React, { useState, useEffect, useCallback } from 'react';
import {
  XCircleIcon,
  EyeIcon,
  UserIcon,
  BuildingOfficeIcon,
  FunnelIcon,
  ArrowPathIcon,
  DocumentMagnifyingGlassIcon,
  ShieldExclamationIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  FolderOpenIcon,
  PaperClipIcon,
} from '@heroicons/react/24/outline';
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext';
import {
  computeTransactionRisk,
  transactionRiskLevelColor,
  transactionRiskBarClass,
} from './payoutTransactionRisk';

/** AU floor cash cap — keep in sync with backend `PAYOUT_CASH_DESK_CAP_AUD` (payout-monitoring.js). */
const PAYOUT_CASH_DESK_CAP_AUD = 5000

function playerTypeLabel(playerType) {
  if (playerType === 'member') return 'Member'
  if (playerType === 'visitor') return 'Visitor'
  return 'Uncarded'
}

function playerTypeBadgeClass(playerType) {
  if (playerType === 'member') return 'bg-blue-100 text-blue-800'
  if (playerType === 'visitor') return 'bg-gray-100 text-gray-700'
  return 'bg-amber-100 text-amber-900'
}

function patronIdSummary(payout) {
  if (payout.playerType === 'visitor') {
    const parts = []
    if (payout.visitId) parts.push(`Visit ${payout.visitId}`)
    if (payout.visitorId) parts.push(`VMS ${payout.visitorId}`)
    return parts.length ? parts.join(' · ') : 'No visit id'
  }
  if (payout.playerType === 'member' && payout.memberId) return `Member ${payout.memberId}`
  return 'No club id'
}

function formatPatronResolvePatron(pr) {
  const p = pr?.patron
  if (!p || typeof p !== 'object') return null
  const first = p.firstname || p.first_name || p.FirstName || ''
  const last = p.lastname || p.last_name || p.LastName || ''
  const name = [first, last].filter(Boolean).join(' ')
  return {
    name: name || null,
    dob: p.dob || p.date_of_birth || p.DateOfBirth || null,
    tier: p.tier_level || p.TierLevel || null,
    status: p.status || p.Status || null,
    responseCode: p.response_code || p.ResponseCode || pr.response_code || null,
  }
}

/**
 * Derive split from total + payment method + optional mobile capture numbers.
 * Matches backend `derivePaymentSplitAmounts` behaviour for display.
 */
function deriveSplitFromAmountAndMethod(total, pmRaw, cashPaid, balance) {
  if (!Number.isFinite(total) || total < 0) return null
  const pm = String(pmRaw || '').trim()
  const pmLower = pm.toLowerCase()
  const isCheque = pmLower === 'cheque'
  const isEft = pmLower === 'eft'
  const isCash = pmLower === 'cash'

  if (isCheque) {
    if (total > PAYOUT_CASH_DESK_CAP_AUD) {
      return {
        cash: PAYOUT_CASH_DESK_CAP_AUD,
        eft: 0,
        cheque: Math.max(0, total - PAYOUT_CASH_DESK_CAP_AUD),
      }
    }
    const c = Number.isFinite(cashPaid) ? Math.min(cashPaid, total) : 0
    const q = Number.isFinite(balance) ? balance : Math.max(0, total - c)
    return { cash: c, eft: 0, cheque: q }
  }

  if (total > PAYOUT_CASH_DESK_CAP_AUD) {
    if (isEft && !Number.isFinite(cashPaid)) {
      return { cash: 0, eft: total, cheque: 0 }
    }
    if (isEft && Number.isFinite(cashPaid) && cashPaid <= 0) {
      return { cash: 0, eft: total, cheque: 0 }
    }
    const cRaw = Number.isFinite(cashPaid)
      ? cashPaid
      : isCash || !pm
        ? PAYOUT_CASH_DESK_CAP_AUD
        : 0
    const c = Math.min(PAYOUT_CASH_DESK_CAP_AUD, Math.max(0, cRaw), total)
    let e = Number.isFinite(balance) ? balance : Math.max(0, total - c)
    if (e + c > total + 0.0001) e = Math.max(0, total - c)
    return { cash: c, eft: e, cheque: 0 }
  }

  if (isCash) {
    const c = Number.isFinite(cashPaid) ? cashPaid : total
    return { cash: Math.min(c, total), eft: 0, cheque: 0 }
  }
  if (isEft) {
    const c = Number.isFinite(cashPaid) ? Math.min(cashPaid, total) : 0
    const e = Number.isFinite(balance) ? balance : Math.max(0, total - c)
    return { cash: c, eft: e, cheque: 0 }
  }

  return {
    cash: Number.isFinite(cashPaid) ? Math.min(cashPaid, total) : total,
    eft: Number.isFinite(balance) ? balance : 0,
    cheque: 0,
  }
}

/**
 * Prefer DB split columns; if missing or all zero with amount > 0, derive from mobile capture or amount + paymentMethod.
 */
function effectivePayoutSplit(payout) {
  const totalAmt = payout.amount != null ? Number(payout.amount) : NaN

  const c0 = payout.cashAmount
  const e0 = payout.eftAmount
  const q0 = payout.chequeAmount
  const hasCol = c0 != null || e0 != null || q0 != null
  if (hasCol) {
    const cash = Number(c0) || 0
    const eft = Number(e0) || 0
    const cheque = Number(q0) || 0
    const sum = cash + eft + cheque
    if (sum > 0.0001 || !Number.isFinite(totalAmt) || totalAmt <= 0) {
      return { cash, eft, cheque, source: 'record' }
    }
    // Stored 0/0/0 but payout has amount — treat as no split and derive below
  }

  const m = payout.mobileCapture && typeof payout.mobileCapture === 'object' ? payout.mobileCapture : null
  const pm = String((m && m.payment_method) || payout.paymentMethod || '').trim()
  const cashPaid =
    m && m.cash_amount_paid != null && m.cash_amount_paid !== ''
      ? Number(m.cash_amount_paid)
      : null
  const balance =
    m && m.balance_to_pay != null && m.balance_to_pay !== ''
      ? Number(m.balance_to_pay)
      : null

  const d = deriveSplitFromAmountAndMethod(totalAmt, pm, cashPaid, balance)
  if (!d) return null
  return {
    ...d,
    source: m ? 'mobile_capture' : 'derived',
  }
}

function formatEftSummary(eft) {
  if (!eft || typeof eft !== 'object') return null
  const region = eft.region ? String(eft.region) : ''
  const bank = eft.bank_name ? String(eft.bank_name) : ''
  const bsb = eft.bsb ? String(eft.bsb) : ''
  const acct = eft.account_number != null ? String(eft.account_number).replace(/\s/g, '') : ''
  const tail = acct.length >= 4 ? acct.slice(-4) : acct
  const parts = []
  if (region) parts.push(region === 'australian' || region === 'Australian' ? 'Australian' : 'Overseas')
  if (bank) parts.push(bank)
  if (bsb) parts.push(`BSB ${bsb}`)
  if (tail) parts.push(`Acct …${tail}`)
  return parts.length ? parts.join(' · ') : null
}

function formatChequeSummary(ch) {
  if (!ch || typeof ch !== 'object') return null
  const name = ch.name_on_cheque ? String(ch.name_on_cheque) : ''
  const dt = ch.cheque_date ? String(ch.cheque_date) : ''
  const hasPhoto = !!(ch.cheque_photo || ch.photo_jpeg_base64)
  const bits = []
  if (name) bits.push(name)
  if (dt) bits.push(dt)
  if (hasPhoto) bits.push('Cheque photo on file')
  return bits.length ? bits.join(' · ') : null
}

/** Signature tile from tablet POST (after blob upload: `image.url`, or legacy base64). */
function formatDeclarationAcceptedAt(iso) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return String(iso)
  }
}

const PayoutMonitoring = () => {
  const { user } = useAuth()
  const [payoutData, setPayoutData] = useState([]);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [payoutModalTab, setPayoutModalTab] = useState('details');
  const [captureAttachments, setCaptureAttachments] = useState(null);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState(null);
  const [creatingCase, setCreatingCase] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [showMiniInvestigation, setShowMiniInvestigation] = useState(false);
  const [miniInvestigationData, setMiniInvestigationData] = useState(null);
  const [listMeta, setListMeta] = useState({ total: null, returned: null, openOnly: true });
  const [filters, setFilters] = useState({
    venue: 'all',
    payoutTier: 'all',
    complianceStatus: 'open',
    kycStatus: 'all',
    riskLevel: 'all',
    playerType: 'all', // 'member' or 'visitor'
    dateRange: 'custom',
    startDate: '',
    endDate: '',
    amountRange: 'all'
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'riskScore', direction: 'desc' }); // Default: risk high to low
  /** Venue names from GET /api/venues — matches payout_records.venue; no hardcoded demo venues */
  const [venueNames, setVenueNames] = useState([]);
  const [txnRiskConfig, setTxnRiskConfig] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/venue-config/transaction-risk'));
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled && j?.success && j.config) setTxnRiskConfig(j.config);
      } catch {/* fall back to defaults inside computeTransactionRisk */}
    })();
    return () => { cancelled = true };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/venues'));
        const j = await r.json();
        if (j.success && Array.isArray(j.data)) {
          const names = j.data
            .map((v) => v.name)
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
          setVenueNames(names);
        }
      } catch (_) {
        setVenueNames([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (filters.venue !== 'all' && venueNames.length > 0 && !venueNames.includes(filters.venue)) {
      setFilters((f) => ({ ...f, venue: 'all' }));
    }
  }, [venueNames, filters.venue]);

  useEffect(() => {
    loadPayoutData();
  }, [filters]);

  /** Re-score rows when transaction-risk config loads (does not re-fetch the list). */
  useEffect(() => {
    if (!txnRiskConfig || payoutData.length === 0) return;
    setPayoutData((rows) =>
      rows.map((row) => {
        const split = effectivePayoutSplit(row);
        const transactionRisk = computeTransactionRisk(row, split, txnRiskConfig);
        return { ...row, transactionRisk, transactionRiskScore: transactionRisk.score };
      })
    );
  }, [txnRiskConfig]);

  const loadPayoutData = async () => {
    setLoading(true);
    try {
      // Always fetch fresh transaction risk config to ensure latest values are used
      let freshTxnRiskConfig = txnRiskConfig;
      try {
        const cfgRes = await authFetch(getLocalApiUrl('/api/venue-config/transaction-risk'));
        if (cfgRes.ok) {
          const cfgJson = await cfgRes.json();
          if (cfgJson?.success && cfgJson.config) {
            freshTxnRiskConfig = cfgJson.config;
            setTxnRiskConfig(cfgJson.config);
          }
        }
      } catch { /* use existing config or defaults */ }

      const params = new URLSearchParams();
      if (filters.venue !== 'all') params.append('venue', filters.venue);
      if (filters.payoutTier !== 'all') params.append('tier', filters.payoutTier);
      if (filters.complianceStatus === 'open') {
        params.append('open_only', '1');
      } else if (filters.complianceStatus !== 'all') {
        params.append('compliance_status', filters.complianceStatus);
      }
      if (filters.startDate) params.append('dateFrom', filters.startDate);
      if (filters.endDate) params.append('dateTo', filters.endDate);
      params.append('enrich', '1');

      const qs = params.toString();

      const payoutsRes = await authFetch(getLocalApiUrl(`/api/monitoring/payouts?${qs}`));
      const payoutsJson = await payoutsRes.json();
      if (payoutsJson.success) {
        const meta = payoutsJson.meta || {};
        setListMeta({
          total: meta.total != null ? Number(meta.total) : null,
          returned: meta.returned != null ? Number(meta.returned) : null,
          openOnly: filters.complianceStatus === 'open',
        });
        let rows = Array.isArray(payoutsJson.data) ? payoutsJson.data : [];
        rows = rows.map((row) => {
          const split = effectivePayoutSplit(row);
          const transactionRisk = computeTransactionRisk(row, split, freshTxnRiskConfig);
          return {
            ...row,
            playerType: row.playerType || 'unknown',
            onWatchlist: row.onWatchlist ?? false,
            watchlistData: row.watchlistData,
            hasCases: row.hasCases ?? false,
            caseCount: row.caseCount ?? 0,
            cases: row.cases ?? [],
            transactionRisk,
            transactionRiskScore: transactionRisk.score,
          };
        });
        
        // Apply client-side filters for KYC, risk level, and player type
        if (filters.kycStatus !== 'all') {
          rows = rows.filter(r => r.kycStatus === filters.kycStatus);
        }
        if (filters.riskLevel !== 'all') {
          if (filters.riskLevel === 'high') rows = rows.filter(r => r.riskScore >= 80);
          else if (filters.riskLevel === 'medium') rows = rows.filter(r => r.riskScore >= 40 && r.riskScore < 80);
          else if (filters.riskLevel === 'low') rows = rows.filter(r => r.riskScore < 40);
        }
        if (filters.playerType !== 'all') {
          rows = rows.filter(r => r.playerType === filters.playerType);
        }
        
        // Sort by risk score (highest first), then by date (newest first), then by id
        const sorted = [...rows].sort((a, b) => {
          // Primary sort: Risk score (highest first)
          const riskA = a.riskScore ?? 0;
          const riskB = b.riskScore ?? 0;
          if (riskB !== riskA) return riskB - riskA;
          
          // Secondary sort: Date (newest first)
          const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
          const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
          if (tb !== ta) return tb - ta;
          
          // Tertiary sort: ID
          return String(b.id || '').localeCompare(String(a.id || ''));
        });
        setPayoutData(sorted);
      }
    } catch (error) {
      console.error('Error loading payout data:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await loadPayoutData();
    setRefreshing(false);
  };

  const closePayoutModal = () => {
    setSelectedPayout(null);
    setPayoutModalTab('details');
    setCaptureAttachments(null);
    setAttachmentsError(null);
    setAttachmentsLoading(false);
  };

  const openPayoutDetail = (payout) => {
    setSelectedPayout(payout);
    setPayoutModalTab('details');
    setCaptureAttachments(null);
    setAttachmentsError(null);
    setAttachmentsLoading(false);
  };

  const loadPayoutAttachments = useCallback(async (payoutId) => {
    if (!payoutId) return;
    setAttachmentsLoading(true);
    setAttachmentsError(null);
    try {
      const r = await authFetch(getLocalApiUrl(`/api/monitoring/payouts/${payoutId}/attachments`));
      const j = await r.json();
      if (!j.success) throw new Error(j.error || 'Failed to load attachments');
      setCaptureAttachments(j.data);
    } catch (e) {
      setCaptureAttachments(null);
      setAttachmentsError(e.message || 'Failed to load attachments');
    } finally {
      setAttachmentsLoading(false);
    }
  }, []);

  const openAttachmentsTab = () => {
    setPayoutModalTab('attachments');
    if (selectedPayout?.id && !captureAttachments && !attachmentsLoading) {
      loadPayoutAttachments(selectedPayout.id);
    }
  };

  const getComplianceStatusBadge = (status) => {
    const badges = {
      COMPLIANT: "bg-green-100 text-green-800",
      NON_COMPLIANT: "bg-red-100 text-red-800",
      PENDING_REVIEW: "bg-yellow-100 text-yellow-800",
      INVESTIGATING: "bg-blue-100 text-blue-800",
      FLAGGED: "bg-orange-100 text-orange-900",
    };
    return badges[status] || "bg-gray-100 text-gray-800";
  };

  const getViolationTypeBadge = (type) => {
    const badges = {
      INSUFFICIENT_AUTHORIZATION: "bg-orange-100 text-orange-800",
      AUTHORIZATION_BYPASS: "bg-red-100 text-red-800",
      VENUE_TIER_VIOLATION: "bg-purple-100 text-purple-800",
      AMOUNT_THRESHOLD_EXCEEDED: "bg-pink-100 text-pink-800"
    };
    return badges[type] || "bg-gray-100 text-gray-800";
  };

  const handleCreateCase = async () => {
    if (!selectedPayout?.id) return;
    setCreatingCase(true);
    try {
      const r = await authFetch(getLocalApiUrl(`/api/monitoring/payouts/${selectedPayout.id}/create-case`), { method: 'POST' });
      const j = await r.json();
      if (!j.success) throw new Error(j.error || 'Failed to create case');
      closePayoutModal();
      loadPayoutData();
      alert(`Case ${j.data?.case_number} created`);
    } catch (e) {
      alert(e.message || 'Failed to create case');
    } finally {
      setCreatingCase(false);
    }
  };

  const handleDismiss = async () => {
    if (!selectedPayout?.id) return;
    if (!confirm('Mark this payout as reviewed and dismiss? It will be marked as COMPLIANT.')) return;
    setDismissing(true);
    try {
      const r = await authFetch(getLocalApiUrl(`/api/monitoring/payouts/${selectedPayout.id}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          compliance_status: 'COMPLIANT', 
          comment_review: `Dismissed by ${user?.name || user?.username || user?.sub || 'staff'} on ${new Date().toLocaleString()}`,
        })
      });
      const j = await r.json();
      if (!j.success) throw new Error(j.error || 'Failed to dismiss');
      closePayoutModal();
      loadPayoutData();
      alert('Payout dismissed and marked as compliant');
    } catch (e) {
      alert(e.message || 'Failed to dismiss');
    } finally {
      setDismissing(false);
    }
  };

  const handleMiniInvestigation = async (payout, e) => {
    e.stopPropagation(); // Prevent row click
    if (payout.playerType !== 'member' || !payout.memberId) {
      alert('Member history is only available when a club member id was recorded on this payout.');
      return;
    }
    
    try {
      // Fetch recent payout history and gaming data
      const [payoutRes, sessionRes] = await Promise.all([
        authFetch(getLocalApiUrl(`/api/monitoring/payouts?member_id=${payout.memberId}&limit=10`)),
        authFetch(getLocalApiUrl(`/api/watchlist/member/${payout.memberId}/overview`))
      ]);
      
      const payoutJson = await payoutRes.json();
      const sessionJson = await sessionRes.json();
      
      setMiniInvestigationData({
        payout,
        recentPayouts: payoutJson.success ? payoutJson.data : [],
        gamingData: sessionJson.success ? sessionJson.data : null
      });
      setShowMiniInvestigation(true);
    } catch (e) {
      alert('Failed to load investigation data: ' + e.message);
    }
  };

  const getRiskScoreColor = (score) => {
    if (score >= 80) return "text-red-600";
    if (score >= 60) return "text-orange-600";
    if (score >= 40) return "text-yellow-600";
    return "text-green-600";
  };

  const kycBadge = (s) => {
    if (!s) return <span className="text-gray-400">—</span>;
    const cls = s === "DONE" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800";
    return <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${cls}`}>{s === "DONE" ? "Done" : "Not done"}</span>;
  };

  const addrBadge = (s) => {
    if (!s) return <span className="text-gray-400">—</span>;
    const map = { VERIFIED: "bg-emerald-100 text-emerald-800", NOT_VERIFIED: "bg-red-100 text-red-800", NA: "bg-gray-100 text-gray-700" };
    const cls = map[s] || "bg-gray-100 text-gray-700";
    const label = s === "VERIFIED" ? "Verified" : s === "NOT_VERIFIED" ? "Not verified" : "N/A";
    return <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${cls}`}>{label}</span>;
  };

  const trunc = (t, n = 48) => {
    if (t == null || t === "") return <span className="text-gray-400">—</span>;
    const s = String(t);
    if (s.length <= n) return <span className="text-gray-800">{s}</span>;
    return <span className="text-gray-800" title={s}>{s.slice(0, n)}…</span>;
  };

  const getAgingDisplay = (timestamp) => {
    if (!timestamp) return { text: '—', color: 'text-gray-400', urgent: false };
    
    const now = new Date();
    const issued = new Date(timestamp);
    const diffMs = now - issued;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    let text, color, urgent;
    
    if (diffHours < 1) {
      text = '<1h';
      color = 'text-green-700';
      urgent = false;
    } else if (diffHours < 24) {
      text = `${diffHours}h`;
      color = diffHours >= 12 ? 'text-amber-700' : 'text-green-700';
      urgent = diffHours >= 12;
    } else if (diffDays < 7) {
      text = `${diffDays}d`;
      color = diffDays >= 3 ? 'text-orange-700' : 'text-amber-700';
      urgent = diffDays >= 3;
    } else {
      const weeks = Math.floor(diffDays / 7);
      text = `${weeks}w`;
      color = 'text-red-700';
      urgent = true;
    }
    
    return { text, color, urgent };
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '⇅';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  // Filters are applied server-side; payoutData is already filtered
  // Apply sorting to payoutData
  const filteredPayouts = [...payoutData].sort((a, b) => {
    const key = sortConfig.key;
    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    
    let aVal = a[key];
    let bVal = b[key];
    
    // Special handling for timestamp (aging)
    if (key === 'timestamp') {
      aVal = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      bVal = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    }
    
    // Special handling for player type
    if (key === 'playerType') {
      aVal = a.memberId ? 'member' : 'visitor';
      bVal = b.memberId ? 'member' : 'visitor';
    }
    
    // Null/undefined handling
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    
    // String comparison
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return direction * aVal.localeCompare(bVal);
    }
    
    // Numeric comparison
    return direction * (aVal - bVal);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const splitForModal =
    selectedPayout != null ? effectivePayoutSplit(selectedPayout) : null
  const mcForModal =
    selectedPayout?.mobileCapture && typeof selectedPayout.mobileCapture === 'object'
      ? selectedPayout.mobileCapture
      : {}
  const eftFromMc = formatEftSummary(mcForModal.eft_details)
  const chequeFromMc = formatChequeSummary(mcForModal.cheque_details)
  const declarationAcceptances = Array.isArray(mcForModal.declaration_acceptances)
    ? mcForModal.declaration_acceptances
    : []
  const attachmentCountHint = Number(selectedPayout?.captureAttachmentCount) || 0
  const licenceImagesRemoved =
    captureAttachments?.licenceImagesRemoved === true ||
    mcForModal.licence_images_retention?.removed === true

  return (
    <div className="space-y-6">
      {/* Top toolbar aligned right */}
      <div className="flex justify-end">
        <button
          onClick={refreshData}
          disabled={refreshing}
          className="flex items-center px-4 py-2 bg-psg-teal text-white rounded-lg hover:bg-psg-teal-600 disabled:opacity-50"
        >
          <ArrowPathIcon className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Filters — one flex-wrap row; dates follow dropdowns and wrap with them */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
            <FunnelIcon className="w-5 h-5 text-gray-400 shrink-0" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>

          <select
            value={venueNames.includes(filters.venue) || filters.venue === 'all' ? filters.venue : 'all'}
            onChange={(e) => setFilters({ ...filters, venue: e.target.value })}
            className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full min-w-0 sm:w-auto sm:min-w-[10rem] sm:max-w-[14rem]"
            title="Venues from master data (venues table)"
          >
            <option value="all">All Venues</option>
            {venueNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>

          <select
            value={filters.payoutTier}
            onChange={(e) => setFilters({...filters, payoutTier: e.target.value})}
            className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full min-w-0 sm:w-auto sm:max-w-[11rem]"
          >
            <option value="all">All tiers (PV1–PV4)</option>
            <option value="PV1">PV1 ($0–$2k)</option>
            <option value="PV2">PV2 ($2k–$5k)</option>
            <option value="PV3">PV3 ($5k–$10k)</option>
            <option value="PV4">PV4 ($10k+)</option>
          </select>

          <select
            value={filters.complianceStatus}
            onChange={(e) => setFilters({...filters, complianceStatus: e.target.value})}
            className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full min-w-0 sm:w-auto sm:max-w-[12rem]"
          >
            <option value="open">Open (needs review)</option>
            <option value="all">All Status</option>
            <option value="COMPLIANT">Compliant / dismissed</option>
            <option value="NON_COMPLIANT">Non-Compliant</option>
            <option value="PENDING_REVIEW">Pending Review</option>
            <option value="FLAGGED">Flagged</option>
            <option value="INVESTIGATING">Investigating</option>
          </select>

          <select
            value={filters.kycStatus}
            onChange={(e) => setFilters({...filters, kycStatus: e.target.value})}
            className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full min-w-0 sm:w-auto sm:max-w-[9rem]"
          >
            <option value="all">All KYC</option>
            <option value="DONE">KYC Done</option>
            <option value="NOT_DONE">KYC Not Done</option>
          </select>

          <select
            value={filters.riskLevel}
            onChange={(e) => setFilters({...filters, riskLevel: e.target.value})}
            className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full min-w-0 sm:w-auto sm:max-w-[11rem]"
          >
            <option value="all">All Risk Levels</option>
            <option value="high">High Risk (80+)</option>
            <option value="medium">Medium Risk (40-79)</option>
            <option value="low">Low Risk (&lt;40)</option>
          </select>

          <select
            value={filters.playerType}
            onChange={(e) => setFilters({...filters, playerType: e.target.value})}
            className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full min-w-0 sm:w-auto sm:max-w-[10rem]"
          >
            <option value="all">All Players</option>
            <option value="member">Members Only</option>
            <option value="visitor">Visitors Only</option>
            <option value="unknown">Uncarded Only</option>
          </select>

          <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
            <CalendarDaysIcon className="w-5 h-5 text-gray-400 shrink-0" />
            <input
              type="date"
              value={filters.startDate}
              onChange={(e)=>setFilters({...filters, startDate: e.target.value})}
              className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-0 w-full sm:w-[9.5rem]"
            />
            <span className="text-xs text-gray-500 shrink-0">to</span>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e)=>setFilters({...filters, endDate: e.target.value})}
              className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-0 w-full sm:w-[9.5rem]"
            />
          </div>
        </div>
      </div>

      {/* Payout Transactions — regulatory-style single view */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-gray-900">Payout transactions</h3>
            {filters.complianceStatus === 'open' ? (
              <span className="inline-flex items-center gap-2 text-sm">
                <span className="font-semibold text-amber-900 bg-amber-100 border border-amber-200 px-2.5 py-0.5 rounded-md">
                  {loading
                    ? 'Loading…'
                    : listMeta.total != null
                      ? `${listMeta.total} open`
                      : `${filteredPayouts.length} open`}
                </span>
                {!loading && listMeta.total != null && listMeta.returned != null && listMeta.returned < listMeta.total && (
                  <span className="text-gray-500">showing {listMeta.returned} (newest first, max 200)</span>
                )}
              </span>
            ) : (
              <span className="text-sm text-gray-500">
                {loading ? 'Loading…' : `${filteredPayouts.length} ${filteredPayouts.length === 1 ? 'entry' : 'entries'}`}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500">
            <span className="inline-block bg-amber-100 text-amber-900 px-2 py-0.5 rounded mr-2">Cash ratio</span>
            highlights cashbox vs payout; <span className="inline-block bg-emerald-100 text-emerald-900 px-2 py-0.5 rounded">AML</span> PEP/TF/PF screening summary.
            <span className="font-medium ml-1">Patron</span> = profile risk; <span className="font-medium">Txn</span> = transaction heuristic (amount / cash / tier / win type).
            Rows at <span className="font-medium">$5,000</span> threshold may be highlighted.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-[1400px] w-full border-collapse divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th onClick={() => handleSort('venue')} className="sticky left-0 z-20 bg-gray-50 px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-300 cursor-pointer hover:bg-gray-100 w-[120px] min-w-[120px]">
                  Venue {getSortIcon('venue')}
                </th>
                <th
                  title="Patron / profile risk from screening"
                  onClick={() => handleSort('riskScore')}
                  className="sticky left-[120px] z-20 bg-gray-50 px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-300 cursor-pointer hover:bg-gray-100 w-[56px] min-w-[56px]"
                >
                  Patron {getSortIcon('riskScore')}
                </th>
                <th
                  title="Transaction risk: amount, cash mix, tier, win type"
                  onClick={() => handleSort('transactionRiskScore')}
                  className="sticky left-[176px] z-20 bg-gray-50 px-2 py-2 text-left font-medium text-gray-600 border-r border-gray-300 cursor-pointer hover:bg-gray-100 w-[48px] min-w-[48px]"
                >
                  Txn {getSortIcon('transactionRiskScore')}
                </th>
                <th onClick={() => handleSort('timestamp')} className="sticky left-[224px] z-20 bg-gray-50 px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-300 cursor-pointer hover:bg-gray-100 w-[80px] min-w-[80px]">
                  Aging {getSortIcon('timestamp')}
                </th>
                <th onClick={() => handleSort('playerType')} className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200 cursor-pointer hover:bg-gray-100">
                  Player {getSortIcon('playerType')}
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200">Receipt</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200">Slip #</th>
                <th onClick={() => handleSort('timestamp')} className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200 cursor-pointer hover:bg-gray-100">
                  Issued {getSortIcon('timestamp')}
                </th>
                <th onClick={() => handleSort('memberName')} className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200 cursor-pointer hover:bg-gray-100">
                  Patron {getSortIcon('memberName')}
                </th>
                <th onClick={() => handleSort('amount')} className="px-3 py-2 text-right font-medium text-gray-600 border-r border-gray-200 cursor-pointer hover:bg-gray-100">
                  Amount {getSortIcon('amount')}
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200">Type</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200">EGM Machine</th>
                <th onClick={() => handleSort('kycStatus')} className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200 cursor-pointer hover:bg-gray-100">
                  KYC {getSortIcon('kycStatus')}
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200">Address</th>
                <th onClick={() => handleSort('cashRatioPct')} className="px-3 py-2 text-right font-medium text-amber-900 bg-amber-50/80 border-r border-amber-200/80 cursor-pointer hover:bg-amber-100">
                  Cash ratio {getSortIcon('cashRatioPct')}
                </th>
                <th className="px-3 py-2 text-left font-medium text-emerald-900 bg-emerald-50/80 border-r border-emerald-200/80">AML check</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200 max-w-[140px]">Capture comment</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200 max-w-[140px]">Review comment</th>
                <th onClick={() => handleSort('payoutTier')} className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200 cursor-pointer hover:bg-gray-100">
                  Tier {getSortIcon('payoutTier')}
                </th>
                <th onClick={() => handleSort('complianceStatus')} className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200 cursor-pointer hover:bg-gray-100">
                  Compliance {getSortIcon('complianceStatus')}
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPayouts.map((payout) => {
                const atThreshold = payout.amount != null && Math.abs(Number(payout.amount) - 5000) < 0.01;
                const aging = getAgingDisplay(payout.timestamp);
                return (
                  <tr
                    key={payout.id}
                    onClick={() => openPayoutDetail(payout)}
                    className={`cursor-pointer ${atThreshold ? 'bg-orange-50/90' : ''}`}
                  >
                    <td className={`sticky left-0 z-20 px-3 py-2 border-r border-gray-300 whitespace-nowrap w-[120px] min-w-[120px] ${atThreshold ? 'bg-orange-50 hover:bg-orange-100' : 'bg-white hover:bg-gray-50'}`}>
                      <span className="text-gray-900 font-medium">{payout.venue}</span>
                    </td>
                    <td className={`sticky left-[120px] z-20 px-3 py-2 border-r border-gray-300 whitespace-nowrap w-[56px] min-w-[56px] ${atThreshold ? 'bg-orange-50 hover:bg-orange-100' : 'bg-white hover:bg-gray-50'}`}>
                      <span className={`font-bold ${getRiskScoreColor(payout.riskScore)}`} title="Patron / profile risk">
                        {payout.riskScore ?? '—'}
                      </span>
                    </td>
                    <td
                      className={`sticky left-[176px] z-20 px-2 py-2 border-r border-gray-300 whitespace-nowrap w-[48px] min-w-[48px] ${atThreshold ? 'bg-orange-50 hover:bg-orange-100' : 'bg-white hover:bg-gray-50'}`}
                      title={payout.transactionRisk?.factors?.length ? payout.transactionRisk.factors.map((f) => f.detail).join(' · ') : 'Transaction risk'}
                    >
                      <span className={`font-bold ${payout.transactionRisk ? transactionRiskLevelColor(payout.transactionRisk.level) : 'text-gray-400'}`}>
                        {payout.transactionRisk?.score ?? '—'}
                      </span>
                    </td>
                    <td className={`sticky left-[224px] z-20 px-3 py-2 border-r border-gray-300 whitespace-nowrap w-[80px] min-w-[80px] ${atThreshold ? 'bg-orange-50 hover:bg-orange-100' : 'bg-white hover:bg-gray-50'}`}>
                      <span className={`font-bold ${aging.color} ${aging.urgent ? 'animate-pulse' : ''}`}>
                        {aging.text}
                      </span>
                    </td>
                    <td className={`px-3 py-2 border-r border-gray-200 whitespace-nowrap ${atThreshold ? 'bg-orange-50/90' : ''}`}>
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${playerTypeBadgeClass(payout.playerType)}`}>
                        {playerTypeLabel(payout.playerType)}
                      </span>
                      {(payout.enrichmentFlags || []).some((f) => f.code === 'member_posed_as_visitor') && (
                        <span className="inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-orange-100 text-orange-900 ml-1" title="DimPatron name match — member card not used">
                          No card
                        </span>
                      )}
                    </td>
                    <td className={`px-3 py-2 border-r border-gray-200 text-gray-800 whitespace-nowrap ${atThreshold ? 'bg-orange-50/90' : ''}`}>{payout.receiptRef || '—'}</td>
                    <td className={`px-3 py-2 border-r border-gray-200 text-gray-800 font-mono whitespace-nowrap ${atThreshold ? 'bg-orange-50/90' : ''}`}>{payout.slipNumber || '—'}</td>
                    <td className={`px-3 py-2 border-r border-gray-200 text-gray-700 whitespace-nowrap ${atThreshold ? 'bg-orange-50/90' : ''}`}>
                      {payout.timestamp ? new Date(payout.timestamp).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                    </td>
                    <td className={`px-3 py-2 border-r border-gray-200 min-w-[120px] ${atThreshold ? 'bg-orange-50/90' : ''}`}>
                      <div className="text-gray-900 font-medium flex items-center gap-2">
                        {payout.memberName}
                        {payout.onWatchlist && (
                          <ExclamationTriangleIcon className="w-4 h-4 text-amber-600" title="On Watchlist" />
                        )}
                        {payout.hasCases && (
                          <FolderOpenIcon className="w-4 h-4 text-blue-600" title={`${payout.caseCount} case(s)`} />
                        )}
                      </div>
                      <div className="text-gray-500 text-xs">{patronIdSummary(payout)}</div>
                    </td>
                    <td className={`px-3 py-2 border-r border-gray-200 text-right font-semibold text-gray-900 whitespace-nowrap ${atThreshold ? 'bg-orange-50/90' : ''}`}>
                      {new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(payout.amount)}
                    </td>
                    <td className={`px-3 py-2 border-r border-gray-200 text-gray-800 whitespace-nowrap ${atThreshold ? 'bg-orange-50/90' : ''}`}>{payout.gameType || '—'}</td>
                    <td className={`px-3 py-2 border-r border-gray-200 text-gray-800 font-mono whitespace-nowrap ${atThreshold ? 'bg-orange-50/90' : ''}`}>{payout.egmId || payout.gameId || '—'}</td>
                    <td className={`px-3 py-2 border-r border-gray-200 whitespace-nowrap ${atThreshold ? 'bg-orange-50/90' : ''}`}>{kycBadge(payout.kycStatus)}</td>
                    <td className={`px-3 py-2 border-r border-gray-200 whitespace-nowrap ${atThreshold ? 'bg-orange-50/90' : ''}`}>{addrBadge(payout.addressVerification)}</td>
                    <td className={`px-3 py-2 border-r border-amber-200/80 text-right bg-amber-50/50 text-amber-950 whitespace-nowrap ${atThreshold ? '!bg-orange-50/90' : ''}`}>
                      {payout.cashRatioPct != null ? `${Number(payout.cashRatioPct).toFixed(2)}%` : '—'}
                    </td>
                    <td className={`px-3 py-2 border-r border-emerald-200/80 bg-emerald-50/50 text-emerald-950 max-w-[200px] ${atThreshold ? '!bg-orange-50/90' : ''}`}>
                      {payout.amlRisk ? <span title={payout.amlRisk}>{trunc(payout.amlRisk, 64)}</span> : <span className="text-gray-400">—</span>}
                    </td>
                    <td className={`px-3 py-2 border-r border-gray-200 text-gray-800 max-w-[160px] ${atThreshold ? 'bg-orange-50/90' : ''}`}>{trunc(payout.commentCapture, 56)}</td>
                    <td className={`px-3 py-2 border-r border-gray-200 text-gray-800 max-w-[160px] ${atThreshold ? 'bg-orange-50/90' : ''}`}>{trunc(payout.commentReview, 56)}</td>
                    <td className={`px-3 py-2 border-r border-gray-200 text-gray-700 whitespace-nowrap ${atThreshold ? 'bg-orange-50/90' : ''}`}>{payout.payoutTier}</td>
                    <td className={`px-3 py-2 border-r border-gray-200 whitespace-nowrap ${atThreshold ? 'bg-orange-50/90' : ''}`}>
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getComplianceStatusBadge(payout.complianceStatus)}`}>
                        {payout.complianceStatus?.replace(/_/g, ' ') || '—'}
                      </span>
                    </td>
                    <td className={`px-3 py-2 border-r border-gray-200 whitespace-nowrap ${atThreshold ? 'bg-orange-50/90' : ''}`}>
                      {payout.complianceStatus === 'NON_COMPLIANT' && (
                        <ShieldExclamationIcon className="w-4 h-4 inline text-red-500" title="Non-compliant" />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payout Detail Modal - Redesigned */}
      {selectedPayout && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-8 mx-auto p-5 border w-11/12 max-w-6xl shadow-2xl rounded-lg bg-white mb-8">
            {/* Header with badges */}
            <div className="flex justify-between items-start mb-4 pb-3 border-b border-gray-300">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">
                  Payout Details - {selectedPayout.id}
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-base font-semibold text-gray-700">{selectedPayout.memberName}</span>
                  {selectedPayout.onWatchlist && (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold bg-amber-100 text-amber-800 rounded-full border border-amber-300">
                      <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                      WATCHLIST
                    </span>
                  )}
                  {selectedPayout.hasCases && (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold bg-blue-100 text-blue-800 rounded-full border border-blue-300">
                      <FolderOpenIcon className="w-3 h-3 mr-1" />
                      {selectedPayout.caseCount} CASE{selectedPayout.caseCount > 1 ? 'S' : ''}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap items-center gap-2">
                  <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${playerTypeBadgeClass(selectedPayout.playerType)}`}>
                    {playerTypeLabel(selectedPayout.playerType)}
                  </span>
                  <span>{patronIdSummary(selectedPayout)}</span>
                </div>
                {(selectedPayout.enrichmentFlags || []).map((f) => (
                  <div key={f.code} className="text-xs text-orange-800 bg-orange-50 border border-orange-200 rounded px-2 py-1 mt-1">
                    {f.message}
                  </div>
                ))}
              </div>
              <button
                onClick={closePayoutModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircleIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="flex gap-1 mb-4 border-b border-gray-200">
              <button
                type="button"
                onClick={() => setPayoutModalTab('details')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                  payoutModalTab === 'details'
                    ? 'border-psg-teal text-psg-teal'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Details
              </button>
              <button
                type="button"
                onClick={openAttachmentsTab}
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                  payoutModalTab === 'attachments'
                    ? 'border-psg-teal text-psg-teal'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <PaperClipIcon className="w-4 h-4" />
                Attachments
                {attachmentCountHint > 0 && (
                  <span className="text-xs bg-gray-100 text-gray-700 rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center">
                    {attachmentCountHint}
                  </span>
                )}
              </button>
            </div>

            {payoutModalTab === 'attachments' ? (
              <div className="mb-4 min-h-[200px]">
                {attachmentsLoading && (
                  <div className="flex items-center justify-center py-16 text-sm text-gray-500">
                    <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
                    Loading images…
                  </div>
                )}
                {!attachmentsLoading && attachmentsError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                    {attachmentsError}
                    <button
                      type="button"
                      onClick={() => loadPayoutAttachments(selectedPayout.id)}
                      className="block mt-2 text-psg-teal underline"
                    >
                      Retry
                    </button>
                  </div>
                )}
                {!attachmentsLoading && !attachmentsError && captureAttachments?.licenceImagesRemoved && (
                  <p className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3">
                    Licence images were removed after DVS per venue retention policy
                    {captureAttachments.retentionPolicy
                      ? ` (${String(captureAttachments.retentionPolicy).replace(/_/g, ' ')})`
                      : ''}
                    .
                  </p>
                )}
                {!attachmentsLoading && !attachmentsError && (
                  <>
                    {Array.isArray(captureAttachments?.attachments) &&
                    captureAttachments.attachments.length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {captureAttachments.attachments.map((tile) => (
                          <div
                            key={tile.key}
                            className="rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm"
                          >
                            <div
                              className="text-xs text-slate-600 px-2 py-1.5 border-b border-slate-100 truncate font-medium"
                              title={tile.label}
                            >
                              {tile.label}
                            </div>
                            <a
                              href={tile.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block bg-slate-100"
                            >
                              <img
                                src={tile.href}
                                alt={tile.label}
                                className="w-full h-36 object-contain"
                              />
                            </a>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 py-8 text-center">
                        No capture images on file for this payout.
                      </p>
                    )}
                  </>
                )}
              </div>
            ) : (
            <>
            <div className="grid grid-cols-3 gap-4 mb-4">
              {/* LEFT: Payment Receipt Panel */}
              <div className="col-span-1 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-300 shadow-sm">
                <div className="text-center mb-3 pb-2 border-b border-dashed border-gray-400">
                  <h4 className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1">Payment Receipt</h4>
                  <div className="text-2xl font-bold text-green-700">
                    {new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(selectedPayout.amount)}
                  </div>
                  <div className="text-xs text-gray-500">{selectedPayout.payoutTier}</div>
                </div>
                
                {/* Payment method + split (DB columns or derived from mobile_capture_json) */}
                <div className="mb-3 pb-3 border-b border-gray-300">
                  <h5 className="text-xs font-bold text-gray-600 uppercase mb-1.5">Payment</h5>
                  {(mcForModal.payment_method || selectedPayout.paymentMethod) && (
                    <div className="flex justify-between text-xs mb-2">
                      <span className="text-gray-600">Method</span>
                      <span className="text-gray-900 font-semibold">
                        {String(mcForModal.payment_method || selectedPayout.paymentMethod || '—')}
                      </span>
                    </div>
                  )}
                  {splitForModal ? (
                    <>
                      {splitForModal.source === 'mobile_capture' && (
                        <p className="text-[10px] text-gray-500 mb-1.5 leading-snug">
                          Split from floor capture (mobile); DB split columns were empty.
                        </p>
                      )}
                      {splitForModal.source === 'derived' && (
                        <p className="text-[10px] text-gray-500 mb-1.5 leading-snug">
                          Split inferred from total and payment method ($5k cash desk cap); not stored on the payout row.
                        </p>
                      )}
                      <div className="space-y-1">
                        {splitForModal.cash > 0 && (
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600">Cash</span>
                            <span className={`font-semibold ${splitForModal.cash > 5000 ? 'text-red-600' : 'text-gray-900'}`}>
                              {new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(splitForModal.cash)}
                              {splitForModal.cash > 5000 && (
                                <span className="ml-1 text-red-600 font-bold">⚠ {'>$5k'}</span>
                              )}
                            </span>
                          </div>
                        )}
                        {splitForModal.eft > 0 && (
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600">EFT</span>
                            <span className="text-gray-900 font-semibold">
                              {new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(splitForModal.eft)}
                            </span>
                          </div>
                        )}
                        {splitForModal.cheque > 0 && (
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600">Cheque</span>
                            <span className="text-gray-900 font-semibold">
                              {new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(splitForModal.cheque)}
                            </span>
                          </div>
                        )}
                        {splitForModal.cash === 0 && splitForModal.eft === 0 && splitForModal.cheque === 0 && (
                          <p className="text-xs text-gray-500">No cash / EFT / cheque amounts recorded.</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="text-xs text-gray-500">No payment split on file for this payout.</p>
                  )}
                  {(eftFromMc || chequeFromMc) && (
                    <div className="mt-2 pt-2 border-t border-gray-200 space-y-1 text-[11px] text-gray-700">
                      {eftFromMc && (
                        <div>
                          <span className="font-semibold text-gray-600">EFT details </span>
                          <span className="break-words">{eftFromMc}</span>
                        </div>
                      )}
                      {chequeFromMc && (
                        <div>
                          <span className="font-semibold text-gray-600">Cheque </span>
                          <span className="break-words">{chequeFromMc}</span>
                        </div>
                      )}
                    </div>
                  )}
                  {(selectedPayout.bankName || selectedPayout.bankAccountLast4) && (
                    <div className="mt-2 pt-2 border-t border-gray-300">
                      <div className="text-xs text-gray-600 space-y-0.5">
                        {selectedPayout.bankName && (
                          <div className="flex justify-between gap-2">
                            <span>Bank</span>
                            <span className="text-gray-900 font-medium text-right">{selectedPayout.bankName}</span>
                          </div>
                        )}
                        {selectedPayout.bankAccountLast4 && (
                          <div className="flex justify-between">
                            <span>Account</span>
                            <span className="text-gray-900 font-mono">****{selectedPayout.bankAccountLast4}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between py-1 border-b border-gray-300">
                    <span className="text-gray-600">Receipt:</span>
                    <span className="text-gray-900 font-mono">{selectedPayout.receiptRef || '—'}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-gray-300">
                    <span className="text-gray-600">Slip:</span>
                    <span className="text-gray-900 font-mono">{selectedPayout.slipNumber || '—'}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-gray-300">
                    <span className="text-gray-600">Issued:</span>
                    <span className="text-gray-900">{selectedPayout.timestamp ? new Date(selectedPayout.timestamp).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-gray-300">
                    <span className="text-gray-600">Venue:</span>
                    <span className="text-gray-900 font-semibold">{selectedPayout.venue}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-gray-300">
                    <span className="text-gray-600">Game:</span>
                    <span className="text-gray-900">{selectedPayout.gameType || '—'}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-gray-300">
                    <span className="text-gray-600">EGM Machine:</span>
                    <span className="text-gray-900 font-mono">{selectedPayout.egmId || selectedPayout.gameId || '—'}</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-gray-600">Duration:</span>
                    <span className="text-gray-900 font-semibold">{selectedPayout.sessionDuration}</span>
                  </div>
                </div>
              </div>

              {/* MIDDLE: Patron at payout + risk */}
              <div className="col-span-1 space-y-3">
                {(selectedPayout.patronResolve || selectedPayout.dimPatron) && (
                  <div className="bg-white rounded-lg p-3 border border-indigo-200 shadow-sm">
                    <h4 className="text-xs font-bold text-indigo-900 uppercase tracking-wide mb-2">Patron at payout</h4>
                    {selectedPayout.patronResolve && (() => {
                      const fmt = formatPatronResolvePatron(selectedPayout.patronResolve)
                      const pr = selectedPayout.patronResolve
                      return (
                        <div className="text-xs text-gray-800 space-y-1 mb-2 pb-2 border-b border-indigo-100">
                          <div className="text-[10px] text-indigo-700 font-semibold uppercase">Floor resolve</div>
                          {pr.lookup_type && (
                            <div><span className="text-gray-500">Lookup:</span> {pr.lookup_type} {pr.lookup_value || ''}</div>
                          )}
                          {pr.source && <div><span className="text-gray-500">Source:</span> {pr.source}</div>}
                          {fmt?.name && <div><span className="text-gray-500">Name:</span> {fmt.name}</div>}
                          {fmt?.dob && <div><span className="text-gray-500">DOB:</span> {fmt.dob}</div>}
                          {fmt?.tier && <div><span className="text-gray-500">Tier:</span> {fmt.tier}</div>}
                          {fmt?.status && <div><span className="text-gray-500">Status:</span> {fmt.status}</div>}
                        </div>
                      )
                    })()}
                    {selectedPayout.dimPatron && (
                      <div className="text-xs text-gray-800 space-y-1">
                        <div className="text-[10px] text-indigo-700 font-semibold uppercase">DimPatron</div>
                        <div>
                          {[selectedPayout.dimPatron.first_name, selectedPayout.dimPatron.last_name].filter(Boolean).join(' ')}
                          {selectedPayout.dimPatron.member_id ? ` (${selectedPayout.dimPatron.member_id})` : ''}
                        </div>
                        {selectedPayout.dimPatron.tier && (
                          <div><span className="text-gray-500">Tier:</span> {selectedPayout.dimPatron.tier}</div>
                        )}
                        {selectedPayout.dimPatron.name_mismatch && (
                          <div className="text-amber-800 font-medium">Capture name differs from DimPatron</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Risk</h4>
                  
                  <div className="mb-3">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Patron risk (profile)</label>
                    <p className="text-[10px] text-gray-500 mb-1 leading-snug">From server screening (AML watchlist, cases, KYC gaps, etc.).</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2.5 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all ${selectedPayout.riskScore >= 80 ? 'bg-red-600' : selectedPayout.riskScore >= 40 ? 'bg-yellow-500' : 'bg-green-500'}`}
                          style={{ width: `${Math.min(100, Number(selectedPayout.riskScore) || 0)}%` }}
                        />
                      </div>
                      <span className={`text-xl font-bold ${getRiskScoreColor(selectedPayout.riskScore)}`}>
                        {selectedPayout.riskScore ?? '—'}
                      </span>
                    </div>
                  </div>

                  {selectedPayout.transactionRisk && (
                    <div className="mb-3 pt-2 border-t border-gray-100">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Transaction risk (this payout)</label>
                      <p className="text-[10px] text-gray-500 mb-1 leading-snug">
                        Heuristic from amount bands ($5k+), cash ratio, cash at desk cap, tier, and win type — for payout structuring / threshold reviews.
                      </p>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex-1 h-2.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all ${transactionRiskBarClass(selectedPayout.transactionRisk.score)}`}
                            style={{ width: `${selectedPayout.transactionRisk.score}%` }}
                          />
                        </div>
                        <span className={`text-xl font-bold ${transactionRiskLevelColor(selectedPayout.transactionRisk.level)}`}>
                          {selectedPayout.transactionRisk.score}
                        </span>
                        <span className={`text-[10px] font-semibold uppercase ${transactionRiskLevelColor(selectedPayout.transactionRisk.level)}`}>
                          {selectedPayout.transactionRisk.level}
                        </span>
                      </div>
                      {selectedPayout.transactionRisk.suggestInvestigate && (
                        <div className="text-[10px] font-medium text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-2 leading-snug">
                          Review suggested: amount / cash mix or combined patron + transaction signals meet investigation heuristics (e.g. ≥$5k band, high cash, or desk-cap cash).
                        </div>
                      )}
                      {selectedPayout.transactionRisk.factors?.length > 0 && (
                        <ul className="text-[10px] text-gray-700 space-y-1 pl-3 list-disc marker:text-gray-400">
                          {selectedPayout.transactionRisk.factors.map((f) => (
                            <li key={f.code}>
                              <span className="font-medium text-gray-800">{f.label}:</span> {f.detail}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>

                {/* Tablet: signatures + capture toggles (authorization is enforced before complete) */}
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <h4 className="text-xs font-bold text-slate-800 uppercase mb-1.5">Tablet attestation</h4>
                  <p className="text-[10px] text-slate-600 mb-2 leading-snug">
                    Payouts complete only after required steps on the tablet—including signatures—so we show what was captured rather than a separate “who was supposed to sign” list.
                  </p>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between gap-2 items-start">
                      <span className="text-gray-600 shrink-0">Patron phone</span>
                      <span className="text-right">
                        {selectedPayout.patronPhoneRefused === true ? (
                          <span className="text-amber-800 font-medium">Declined to provide</span>
                        ) : mcForModal.patron_phone ? (
                          <span className="text-emerald-800 font-medium">Captured on tablet</span>
                        ) : selectedPayout.patronPhoneRefused === false ? (
                          <span className="text-gray-600">Not captured</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </span>
                    </div>
                    {(String(selectedPayout.paymentMethod || '').toLowerCase() === 'eft' ||
                      selectedPayout.bankAccountPhysicallyVerified !== null) && (
                      <div className="flex justify-between gap-2 items-start">
                        <span className="text-gray-600 shrink-0">Bank details verified in person</span>
                        <span className="text-right">
                          {selectedPayout.bankAccountPhysicallyVerified === true ? (
                            <span className="text-emerald-800 font-medium">Cashier confirmed</span>
                          ) : selectedPayout.bankAccountPhysicallyVerified === false ? (
                            <span className="text-amber-800 font-medium">Not confirmed on tablet</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                  {(attachmentCountHint > 0 ||
                    (selectedPayout.signatureCount != null && selectedPayout.signatureCount > 0)) && (
                    <div className="mt-3 pt-2 border-t border-slate-200">
                      <button
                        type="button"
                        onClick={openAttachmentsTab}
                        className="text-[10px] text-psg-teal font-medium hover:underline inline-flex items-center gap-1"
                      >
                        <PaperClipIcon className="w-3.5 h-3.5" />
                        {attachmentCountHint > 0
                          ? `${attachmentCountHint} capture attachment${attachmentCountHint === 1 ? '' : 's'} — open Attachments tab to view`
                          : 'View capture attachments'}
                      </button>
                    </div>
                  )}
                  {declarationAcceptances.length > 0 && (
                    <div className="mt-3 pt-2 border-t border-slate-200">
                      <p className="text-[10px] font-semibold text-slate-700 mb-1.5">Declaration acceptances</p>
                      <div className="space-y-1.5">
                        {declarationAcceptances.map((row, idx) => {
                          const label = row.label || row.role_id || `Role ${idx + 1}`
                          const accepted = row.accepted === true
                          return (
                            <div
                              key={String(row.slot_key ?? row.role_id ?? idx)}
                              className="rounded border border-slate-200 bg-white px-2 py-1.5 text-[10px]"
                            >
                              <div className="flex justify-between gap-2 items-start">
                                <span className="font-semibold text-slate-800">{label}</span>
                                <span className={accepted ? 'text-emerald-800 font-medium shrink-0' : 'text-amber-800 font-medium shrink-0'}>
                                  {accepted ? 'Accepted' : 'Not accepted'}
                                </span>
                              </div>
                              {row.checkbox_label && (
                                <p className="text-slate-600 mt-0.5 leading-snug">&ldquo;{String(row.checkbox_label)}&rdquo;</p>
                              )}
                              <p className="text-slate-500 mt-0.5">
                                {formatDeclarationAcceptedAt(row.accepted_at)}
                              </p>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT: Compliance, verification & context */}
              <div className="col-span-1 space-y-3">
                <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Compliance & verification</h4>

                  <div className="mb-2">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Compliance status</label>
                    <span className={`inline-flex px-2 py-0.5 text-xs font-bold rounded-full ${getComplianceStatusBadge(selectedPayout.complianceStatus)}`}>
                      {(selectedPayout.complianceStatus || '—').replace(/_/g, ' ')}
                    </span>
                    <p className="text-[10px] text-gray-500 mt-1.5 leading-snug">
                      Updated from linked verification when DVS and GreenID AML complete (compliant / KYC done).
                      PEP/sanctions hits set <span className="font-medium">Flagged</span>. Manual{' '}
                      <span className="font-medium">Dismiss</span> still available for edge cases.
                    </p>
                    {(selectedPayout.verificationQueueId || selectedPayout.queueDvsStatus) && (
                      <p className="text-[10px] text-teal-800 mt-1.5 bg-teal-50 rounded px-2 py-1 border border-teal-100">
                        Verification queue #{selectedPayout.verificationQueueId}: DVS{' '}
                        {selectedPayout.queueDvsStatus || '—'}, AML {selectedPayout.queueAmlStatus || '—'}
                        {selectedPayout.queueOnboardingStatus
                          ? ` (${String(selectedPayout.queueOnboardingStatus).replace(/_/g, ' ')})`
                          : ''}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">KYC</label>
                      {kycBadge(selectedPayout.kycStatus)}
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Address verify</label>
                      {addrBadge(selectedPayout.addressVerification)}
                    </div>
                  </div>

                  {(mcForModal.patron_phone || mcForModal.patron_address) && (
                    <div className="mb-2 pt-2 border-t border-gray-100">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Patron contact (floor capture)</label>
                      <div className="space-y-1 text-xs text-gray-800">
                        {mcForModal.patron_phone && (
                          <div>
                            <span className="text-gray-500">Phone </span>
                            {String(mcForModal.patron_phone)}
                          </div>
                        )}
                        {mcForModal.patron_address && (
                          <div className="whitespace-pre-wrap break-words">
                            <span className="text-gray-500">Address </span>
                            {String(mcForModal.patron_address)}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {licenceImagesRemoved && (
                    <div className="mb-2 text-[10px] text-slate-700 bg-slate-50 rounded px-2 py-1.5 border border-slate-200 leading-snug">
                      Licence images were removed after DVS per venue{' '}
                      <span className="font-medium">Licence image retention</span> policy
                      {mcForModal.licence_images_retention?.policy
                        ? ` (${String(mcForModal.licence_images_retention.policy).replace(/_/g, ' ')})`
                        : ''}
                      .
                    </div>
                  )}

                  <div className="mb-2">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Cash ratio</label>
                    <span className="text-base font-bold text-amber-700">
                      {selectedPayout.cashRatioPct != null ? `${Number(selectedPayout.cashRatioPct).toFixed(2)}%` : '—'}
                    </span>
                  </div>

                  {selectedPayout.occupation && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Occupation</label>
                      <span className="text-xs text-gray-800">{selectedPayout.occupation}</span>
                    </div>
                  )}
                </div>

                {/* AML Check */}
                <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                  <h4 className="text-xs font-bold text-emerald-800 uppercase mb-1">AML Screening</h4>
                  <p className="text-xs text-emerald-900">{selectedPayout.amlRisk || 'No issues detected'}</p>
                </div>

                {/* Watchlist Status */}
                {selectedPayout.onWatchlist && selectedPayout.watchlistData && (
                  <div className="bg-amber-50 rounded-lg p-3 border border-amber-300">
                    <h4 className="text-xs font-bold text-amber-800 uppercase mb-1.5 flex items-center gap-1">
                      <ExclamationTriangleIcon className="w-3 h-3" />
                      Watchlist
                    </h4>
                    <div className="text-xs space-y-0.5">
                      <div className="flex justify-between">
                        <span className="text-amber-700">Added:</span>
                        <span className="font-semibold text-amber-900">
                          {selectedPayout.watchlistData.first_seen ? new Date(selectedPayout.watchlistData.first_seen).toLocaleDateString('en-AU') : '—'}
                        </span>
                      </div>
                      {selectedPayout.watchlistData.reason && (
                        <div className="text-amber-800 mt-1 italic">
                          "{selectedPayout.watchlistData.reason}"
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Previous Cases */}
                {selectedPayout.hasCases && (
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200 max-h-40 overflow-y-auto">
                    <h4 className="text-xs font-bold text-blue-800 uppercase mb-1.5 flex items-center gap-1">
                      <FolderOpenIcon className="w-3 h-3" />
                      Cases ({selectedPayout.caseCount})
                    </h4>
                    <div className="space-y-1.5">
                      {selectedPayout.cases.slice(0, 4).map((c, idx) => (
                        <div key={idx} className="text-xs bg-white rounded p-1.5 border border-blue-100">
                          <div className="font-bold text-blue-900">{c.case_number || c.id}</div>
                          <div className="text-gray-600 text-xs">
                            {c.case_type} • {c.status}
                          </div>
                          <div className="text-gray-500 text-xs">
                            {c.created_at ? new Date(c.created_at).toLocaleDateString('en-AU') : '—'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Comments Section - Side by Side */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-300 mb-4">
              <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wide mb-2">Comments</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Capture Comment (Floor Staff)</label>
                  <div className="bg-white rounded p-2 border border-gray-200 text-xs text-gray-800 min-h-[70px]">
                    {selectedPayout.commentCapture || <span className="text-gray-400 italic">No capture comment</span>}
                  </div>
                  {selectedPayout.patronAddressFromCapture && (
                    <p className="text-[10px] text-gray-500 mt-1 whitespace-pre-wrap break-words">
                      <span className="font-medium">Patron address (capture):</span>{' '}
                      {selectedPayout.patronAddressFromCapture}
                    </p>
                  )}
                </div>
                
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Review Comment (Compliance)</label>
                  <textarea
                    placeholder="Add compliance review notes..."
                    defaultValue={selectedPayout.commentReview || ''}
                    className="w-full bg-white rounded p-2 border border-gray-300 text-xs text-gray-800 min-h-[70px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
            </>
            )}

            {/* Actions */}
            <div className="flex justify-end items-center pt-3 border-t border-gray-200">
              <div className="flex space-x-2">
                <button
                  onClick={handleDismiss}
                  disabled={dismissing}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-semibold text-sm"
                >
                  {dismissing ? 'Dismissing…' : 'Dismiss'}
                </button>
                <button
                  onClick={handleCreateCase}
                  disabled={creatingCase}
                  className="px-4 py-2 bg-psg-teal text-white rounded-lg hover:bg-psg-teal-600 disabled:opacity-50 font-semibold text-sm"
                >
                  {creatingCase ? 'Starting…' : 'Start Investigation'}
                </button>
                <button
                  onClick={closePayoutModal}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutMonitoring;